import React, { useState } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import ConfirmationModal from './ConfirmationModal';
import {
  DocumentArrowDownIcon,
  XMarkIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

interface ProjectItem {
  id: number;
  project_id: number;
  name: string;
  description: string;
  quantity: number;
  position_order: number;
  variants?: ItemVariant[];
}

interface ItemVariant {
  id: number;
  project_item_id: number;
  name: string;
  description: string;
  price: number | string;
  image_path?: string;
  link?: string;
  vote_count: number;
  total_votes: number;
  my_vote?: number;
}

interface Project {
  id: number;
  name: string;
  description: string;
  status: string;
  created_at: string;
  creator_name: string;
  image_path?: string;
  min_price?: number;
  max_price?: number;
  variant_count?: number;
  items?: ProjectItem[];
}

interface ProjectsExportProps {
  isOpen: boolean;
  onClose: () => void;
  project: Project | null;
}

const ProjectsExport: React.FC<ProjectsExportProps> = ({ isOpen, onClose, project }) => {
  const [loading, setLoading] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    type: 'success' as 'success' | 'error',
    title: '',
    message: '',
    variant: 'single' as 'single',
  });

  // Hilfsfunktion um String oder Number zu Number zu konvertieren
  const toNumber = (value: string | number | undefined | null): number => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return value;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  };

  // Hilfsfunktion um URLs zu kürzen
  const shortenUrl = (url: string): string => {
    if (!url) return '';
    try {
      const urlObj = new URL(url);
      const domain = urlObj.hostname.replace('www.', '');
      const path = urlObj.pathname;

      // Wenn der Pfad kurz ist, zeige Domain + Pfad
      if (path.length <= 20) {
        return domain + path;
      }

      // Sonst zeige nur Domain + ...
      return domain + '/...';
    } catch {
      // Falls URL ungültig ist, kürze den String
      if (url.length > 30) {
        return url.substring(0, 27) + '...';
      }
      return url;
    }
  };

  const generatePDF = async (forPreview = false) => {
    if (!project) return;

    setLoading(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 10;
      const contentWidth = 190;
      const headerHeight = 30;
      const footerHeight = 12;

      let currentPage = 1;
      let totalPages = 1;

      const addHeader = (pageNumber: number) => {
        // Grüner Header-Balken
        pdf.setFillColor(34, 197, 94); // Tailwind success-500
        pdf.rect(margin, margin, contentWidth, 25, 'F');

        // Vereinsname
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(16);
        pdf.setTextColor(255, 255, 255);
        pdf.text('HSV Hundesportverein', margin + 5, margin + 12);

        // Dokumenttitel
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(12);
        pdf.text('Projektstückliste', margin + 5, margin + 20);

        // Projektname
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        const maxWidth = contentWidth - 60;
        const lines = pdf.splitTextToSize(project.name, maxWidth);
        pdf.text(lines[0], pageWidth - margin - 5, margin + 12, { align: 'right' });

        // Seitenzahl
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Seite ${pageNumber} von ${totalPages}`, pageWidth - margin - 5, margin + 20, { align: 'right' });
      };

      const addFooter = () => {
        pdf.setLineWidth(0.3);
        pdf.setDrawColor(34, 197, 94);
        pdf.line(margin, pageHeight - margin - 5, pageWidth - margin, pageHeight - margin - 5);

        pdf.setFont('helvetica', 'italic');
        pdf.setFontSize(8);
        pdf.setTextColor(75, 85, 99);

        const footerText = 'HSV Hundesportverein – Projektverwaltung';
        pdf.text(footerText, pageWidth / 2, pageHeight - margin, { align: 'center' });
      };

      let currentY = margin + headerHeight + 5;

      // Projektinfo
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(14);
      pdf.setTextColor(17, 24, 39);
      pdf.text(project.name, margin, currentY);
      currentY += 8;

      if (project.description) {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        pdf.setTextColor(75, 85, 99);
        const descLines = pdf.splitTextToSize(project.description, contentWidth);
        pdf.text(descLines, margin, currentY);
        currentY += descLines.length * 5 + 5;
      }

      // Status und Ersteller
      pdf.setFontSize(9);
      pdf.setTextColor(107, 114, 128);
      pdf.text(`Status: ${project.status === 'active' ? 'Aktiv' : 'Archiviert'}`, margin, currentY);
      pdf.text(`Erstellt von: ${project.creator_name}`, margin + 60, currentY);
      currentY += 5;

      // Preisspanne berechnen
      let minPrice = Infinity;
      let maxPrice = -Infinity;
      let variantCount = 0;

      if (project.items && project.items.length > 0) {
        for (const item of project.items) {
          if (item.variants && item.variants.length > 0) {
            for (const variant of item.variants) {
              const price = toNumber(variant.price) * item.quantity;
              if (price < minPrice) minPrice = price;
              if (price > maxPrice) maxPrice = price;
              variantCount++;
            }
          }
        }
      }

      // Preisspanne anzeigen
      if (variantCount > 0 && minPrice !== Infinity) {
        pdf.setFontSize(10);
        pdf.setTextColor(34, 197, 94); // Grün
        pdf.setFont('helvetica', 'bold');

        if (minPrice === maxPrice) {
          pdf.text(`Preisspanne: ${minPrice.toFixed(2)} €`, margin, currentY);
        } else {
          pdf.text(`Preisspanne: ${minPrice.toFixed(2)} € - ${maxPrice.toFixed(2)} €`, margin, currentY);
        }

        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(107, 114, 128);
        pdf.setFontSize(8);
        pdf.text(`(${variantCount} ${variantCount === 1 ? 'Variante' : 'Varianten'})`, margin + 70, currentY);

        currentY += 5;
      }

      currentY += 10;

      // Für jedes Item eine Tabelle erstellen
      if (project.items && project.items.length > 0) {
        for (const item of project.items) {
          // Prüfe ob noch genug Platz auf der Seite ist
          if (currentY > pageHeight - 80) {
            pdf.addPage();
            currentPage++;
            currentY = margin + headerHeight + 5;
          }

          // Item Header
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(12);
          pdf.setTextColor(17, 24, 39);
          const itemText = `${item.name} (${item.quantity}x)`;
          pdf.text(itemText, margin, currentY);
          currentY += 5;

          if (item.description) {
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(9);
            pdf.setTextColor(75, 85, 99);
            const descLines = pdf.splitTextToSize(item.description, contentWidth);
            pdf.text(descLines, margin, currentY);
            currentY += descLines.length * 4 + 2;
          }

          // Varianten Tabelle
          if (item.variants && item.variants.length > 0) {
            const tableData = item.variants.map(variant => {
              // Beschreibung mit Link kombinieren
              let descriptionText = variant.description || '-';
              if (variant.link) {
                if (descriptionText === '-') {
                  descriptionText = `>> Link`;
                } else {
                  descriptionText = `${descriptionText}\n>> Link`;
                }
              }

              return [
                variant.name,
                descriptionText,
                `${toNumber(variant.price).toFixed(2)} €`,
                `${(toNumber(variant.price) * item.quantity).toFixed(2)} €`,
                variant.total_votes.toString(),
              ];
            });

            autoTable(pdf, {
              startY: currentY,
              head: [['Variante', 'Beschreibung / Link', 'Preis/Stück', 'Gesamt', 'Stimmen']],
              body: tableData,
              theme: 'grid',
              styles: {
                font: 'helvetica',
                fontSize: 8,
                textColor: [17, 24, 39],
                lineColor: [209, 213, 219],
                lineWidth: 0.25,
                cellPadding: 2,
                halign: 'left',
                valign: 'top',
              },
              headStyles: {
                fillColor: [243, 244, 246],
                textColor: [17, 24, 39],
                fontStyle: 'bold',
                fontSize: 9,
                cellPadding: 3,
                valign: 'middle',
              },
              columnStyles: {
                0: { cellWidth: 35 }, // Variante
                1: { cellWidth: 75 }, // Beschreibung / Link
                2: { cellWidth: 25, halign: 'right' }, // Preis/Stück
                3: { cellWidth: 25, halign: 'right' }, // Gesamt
                4: { cellWidth: 20, halign: 'center' }, // Stimmen
              },
              alternateRowStyles: {
                fillColor: [249, 250, 251],
              },
              margin: { left: margin, right: margin },
            });

            currentY = (pdf as any).lastAutoTable.finalY + 8;
          } else {
            pdf.setFont('helvetica', 'italic');
            pdf.setFontSize(9);
            pdf.setTextColor(107, 114, 128);
            pdf.text('Keine Varianten vorhanden', margin + 5, currentY);
            currentY += 10;
          }
        }
      } else {
        pdf.setFont('helvetica', 'italic');
        pdf.setFontSize(10);
        pdf.setTextColor(107, 114, 128);
        pdf.text('Keine Positionen vorhanden', margin, currentY);
      }

      // Header und Footer für alle Seiten setzen
      totalPages = (pdf as any).internal.getNumberOfPages();
      currentPage = 1;
      for (let i = 1; i <= totalPages; i++) {
        (pdf as any).setPage(i);
        addHeader(currentPage);
        addFooter();
        currentPage++;
      }

      if (forPreview) {
        const blob = pdf.output('blob');
        setPdfBlob(blob);
        setIsPreviewOpen(true);
      } else {
        pdf.save(`${project.name.replace(/[^a-z0-9äöüÄÖÜß\s]/gi, '_').replace(/\s+/g, '_')}.pdf`);
        setConfirmModal({
          isOpen: true,
          type: 'success',
          title: 'Export erfolgreich',
          message: 'Die PDF-Datei wurde erfolgreich heruntergeladen.',
          variant: 'single'
        });
      }
    } catch (error) {
      console.error('Fehler beim PDF-Export:', error);
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Export fehlgeschlagen',
        message: 'Die PDF-Datei konnte nicht erstellt werden.',
        variant: 'single'
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = () => {
    generatePDF(true);
  };

  const closePreview = () => {
    setIsPreviewOpen(false);
    setPdfBlob(null);
  };

  const downloadPdfFromPreview = () => {
    if (pdfBlob && project) {
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${project.name.replace(/[^a-z0-9äöüÄÖÜß\s]/gi, '_').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setConfirmModal({
        isOpen: true,
        type: 'success',
        title: 'Export erfolgreich',
        message: 'Die PDF-Datei wurde erfolgreich heruntergeladen.',
        variant: 'single'
      });
    }
  };

  const handleClose = () => {
    closePreview();
    onClose();
  };

  if (!isOpen || !project) return null;

  return (
    <>
      {/* Modal für Export-Optionen */}
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-lg p-8 m-4">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-3">
              <DocumentArrowDownIcon className="h-8 w-8 text-primary-600" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Projekt exportieren
              </h3>
            </div>
            <button
              onClick={handleClose}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label="Modal schließen"
            >
              <XMarkIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>

          {/* Projektinfo */}
          <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
              {project.name}
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {project.description}
            </p>
            {project.items && project.items.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {project.items.length} {project.items.length === 1 ? 'Position' : 'Positionen'}
                  {project.variant_count && project.variant_count > 0 && (
                    <> · {project.variant_count} {project.variant_count === 1 ? 'Variante' : 'Varianten'}</>
                  )}
                </p>
              </div>
            )}
          </div>

          {/* Info */}
          <div className="mb-6 p-4 bg-info-50 dark:bg-info-900/20 rounded-lg">
            <p className="text-sm text-info-800 dark:text-info-300">
              <strong>Hinweis:</strong> Das PDF enthält alle Positionen und Varianten mit Preisen und Abstimmungsergebnissen.
            </p>
          </div>

          {/* Export Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handlePreview}
              disabled={loading}
              className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300
                       rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-all font-medium
                       flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <DocumentTextIcon className="h-5 w-5" />
                  Vorschau
                </>
              )}
            </button>

            <button
              onClick={() => generatePDF(false)}
              disabled={loading}
              className="flex-1 px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700
                       transition-all font-medium flex items-center justify-center gap-2
                       disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <DocumentArrowDownIcon className="h-5 w-5" />
                  PDF Export
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* PDF Vorschau Modal */}
      {isPreviewOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-75 z-[120]"
            onClick={closePreview}
          />

          {/* Modal */}
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 pointer-events-none">
            <div className="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-4xl pointer-events-auto flex flex-col"
                 style={{ height: '80vh', minHeight: '600px', maxHeight: '800px' }}>

              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    Projektstückliste - Vorschau
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {project.name}
                  </p>
                </div>
                <button
                  onClick={closePreview}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <XMarkIcon className="h-6 w-6 text-gray-500" />
                </button>
              </div>

              {/* PDF Content */}
              <div className="flex-1 bg-gray-50 dark:bg-gray-800 p-4 overflow-auto">
                <div className="bg-white rounded-lg border border-gray-200 dark:border-gray-700 p-8 min-h-full">
                  {/* Simulierte PDF-Vorschau */}
                  <div className="max-w-3xl mx-auto">
                    {/* Header */}
                    <div className="bg-success-500 text-white p-4 rounded-t-lg mb-4">
                      <h3 className="text-lg font-bold">HSV Hundesportverein</h3>
                      <p className="text-sm">Projektstückliste - {project.name}</p>
                    </div>

                    {/* Projektinfo */}
                    <div className="mb-6">
                      <h4 className="text-lg font-bold mb-2">{project.name}</h4>
                      {project.description && (
                        <p className="text-sm text-gray-600 mb-2">{project.description}</p>
                      )}
                      <p className="text-xs text-gray-500 mb-2">
                        Status: {project.status === 'active' ? 'Aktiv' : 'Archiviert'} |
                        Erstellt von: {project.creator_name}
                      </p>
                      {project.variant_count && project.variant_count > 0 && (
                        <div className="text-sm font-semibold text-success-600">
                          Preisspanne: {toNumber(project.min_price).toFixed(2)} €
                          {toNumber(project.min_price) !== toNumber(project.max_price) &&
                            ` - ${toNumber(project.max_price).toFixed(2)} €`}
                          <span className="text-xs text-gray-500 ml-2">
                            ({project.variant_count} {project.variant_count === 1 ? 'Variante' : 'Varianten'})
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Items Preview */}
                    {project.items && project.items.slice(0, 2).map((item) => (
                      <div key={item.id} className="mb-6">
                        <h5 className="font-bold text-sm mb-2">
                          {item.name} ({item.quantity}x)
                        </h5>
                        {item.description && (
                          <p className="text-xs text-gray-600 mb-2">{item.description}</p>
                        )}

                        {item.variants && item.variants.length > 0 && (
                          <table className="w-full border-collapse text-xs mb-4">
                            <thead>
                              <tr className="bg-gray-100 border-b border-gray-300">
                                <th className="text-left p-2 border border-gray-300">Variante</th>
                                <th className="text-left p-2 border border-gray-300">Beschreibung / Link</th>
                                <th className="text-right p-2 border border-gray-300">Preis/Stück</th>
                                <th className="text-right p-2 border border-gray-300">Gesamt</th>
                                <th className="text-center p-2 border border-gray-300">Stimmen</th>
                              </tr>
                            </thead>
                            <tbody>
                              {item.variants.slice(0, 3).map((variant, idx) => (
                                <tr key={variant.id} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                                  <td className="p-2 border border-gray-300">{variant.name}</td>
                                  <td className="p-2 border border-gray-300 text-xs">
                                    {variant.description || '-'}
                                    {variant.link && (
                                      <div className="text-info-600 mt-1">
                                        🔗 Link
                                      </div>
                                    )}
                                  </td>
                                  <td className="p-2 border border-gray-300 text-right">
                                    {toNumber(variant.price).toFixed(2)} €
                                  </td>
                                  <td className="p-2 border border-gray-300 text-right">
                                    {(toNumber(variant.price) * item.quantity).toFixed(2)} €
                                  </td>
                                  <td className="p-2 border border-gray-300 text-center">
                                    {variant.total_votes}
                                  </td>
                                </tr>
                              ))}
                              {item.variants.length > 3 && (
                                <tr>
                                  <td colSpan={5} className="p-2 text-center text-gray-500 italic border border-gray-300">
                                    ... und {item.variants.length - 3} weitere Varianten
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        )}
                      </div>
                    ))}

                    {project.items && project.items.length > 2 && (
                      <p className="text-center text-sm text-gray-500 italic">
                        ... und {project.items.length - 2} weitere Positionen
                      </p>
                    )}

                    {/* Footer */}
                    <div className="mt-8 pt-4 border-t-2 border-success-500 text-center text-xs text-gray-600">
                      <p>HSV Hundesportverein – Projektverwaltung</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer with Actions */}
              <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  PDF-Dokument bereit zum Download
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={closePreview}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800
                             hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors text-sm font-medium"
                  >
                    Schließen
                  </button>
                  <button
                    onClick={() => {
                      downloadPdfFromPreview();
                      closePreview();
                    }}
                    className="px-4 py-2 bg-primary-600 text-white hover:bg-primary-700
                             rounded-lg transition-colors text-sm font-medium flex items-center gap-2"
                  >
                    <DocumentArrowDownIcon className="h-4 w-4" />
                    PDF Herunterladen
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        visible={confirmModal.isOpen}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        button={{
          text: 'OK',
          onPress: () => setConfirmModal({ ...confirmModal, isOpen: false })
        }}
      />
    </>
  );
};

export default ProjectsExport;
